import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getToken } from '@shared/services/api';
import { Button, Card, Badge } from '@shared/components/ui';
import { getStatusTone, toneToBadgeVariant } from '@shared/config/theme';
import { toneChipClasses, toneIconBgClasses, toneTextClasses } from '@shared/utils/statusTone';
import {
  Activity,
  Users,
  CreditCard,
  DollarSign,
  Database,
  Server,
  HardDrive,
  AlertTriangle,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { getPlatformHealthTone } from '../utils/platformHealthTone';

interface PlatformSystemHealth {
  database_status: string;
  redis_status: string;
  storage_status: string;
  agent_status: string;
  last_checked: string;
}

interface PlatformEvent {
  id: string;
  type: string;
  message: string;
  created_at: string;
}

interface TopCustomer {
  organization_id: string;
  name: string;
  storage_used: number;
  backup_count: number;
  monthly_revenue: number;
}

interface PlatformAdminDashboardMetrics {
  total_customers: number;
  active_subscriptions: number;
  monthly_recurring_revenue: number;
  system_health: PlatformSystemHealth;
  recent_events: PlatformEvent[];
  top_customers: TopCustomer[];
}

type DashboardErrorKey = 'accessDenied' | 'connectionFailed' | 'loadFailed';

export default function PlatformAdminDashboard() {
  const { t, i18n } = useTranslation('admin');
  const [metrics, setMetrics] = useState<PlatformAdminDashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<DashboardErrorKey | null>(null);
  const token = getToken();

  useEffect(() => {
    fetchDashboardMetrics();
  }, []);

  const fetchDashboardMetrics = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/v1/admin/dashboard', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        setMetrics(await res.json());
        setError(null);
      } else if (res.status === 403) {
        setError('accessDenied');
      } else {
        setError('loadFailed');
      }
    } catch {
      setError('connectionFailed');
    } finally {
      setLoading(false);
    }
  };

  const locale = i18n.resolvedLanguage || 'en';
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' }).format(amount);
  const formatNumber = (value: number, digits = 0) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(value);

  if (loading) {
    return (
      <>
        <div className="py-20 flex justify-center" role="status" aria-live="polite">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
          <span className="sr-only">{t('platformDashboard.loading')}</span>
        </div>
      </>
    );
  }

  if (error || !metrics) {
    const accessDenied = error === 'accessDenied';
    return (
      <>
        <div className="py-20 px-4 text-center" role="alert">
          <div className="text-6xl mb-4" aria-hidden="true">
            {accessDenied ? '🔒' : '⚠️'}
          </div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">
            {t(
              accessDenied
                ? 'platformDashboard.accessDeniedTitle'
                : 'platformDashboard.loadFailedTitle'
            )}
          </h2>
          <p className="text-text-muted-light dark:text-text-muted-dark">
            {t(`platformDashboard.errors.${error ?? 'loadFailed'}`)}
          </p>
          {!accessDenied && (
            <Button className="mt-5" onClick={() => void fetchDashboardMetrics()}>
              {t('platformDashboard.retry')}
            </Button>
          )}
        </div>
      </>
    );
  }

  const systemStatusTone = getPlatformHealthTone(metrics.system_health);
  const isHealthy = systemStatusTone === 'ok';

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700">
        <div>
          <h1 className="app-page-title">{t('platformDashboard.title')}</h1>
          <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-1">
            {t('platformDashboard.subtitle')}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
          <StatCard
            label={t('platformDashboard.totalCustomers')}
            value={formatNumber(metrics.total_customers)}
            icon={<Users className="w-6 h-6" />}
            color="text-state-info-light dark:text-state-info-dark"
            bg="bg-state-info-bg-light dark:bg-state-info-bg-dark"
          />
          <StatCard
            label={t('platformDashboard.activeSubscriptions')}
            value={formatNumber(metrics.active_subscriptions)}
            icon={<CreditCard className="w-6 h-6" />}
            color="text-accent-text-light dark:text-accent-text-dark"
            bg="bg-accent-soft-light dark:bg-accent-soft-dark"
          />
          <StatCard
            label="MRR"
            value={formatCurrency(metrics.monthly_recurring_revenue)}
            icon={<DollarSign className="w-6 h-6" />}
            color="text-state-ok-light dark:text-state-ok-dark"
            bg="bg-state-ok-bg-light dark:bg-state-ok-bg-dark"
          />
          <StatCard
            label={t('platformDashboard.systemStatus')}
            value={t(
              isHealthy
                ? 'platformDashboard.status.operational'
                : 'platformDashboard.status.degraded'
            )}
            icon={
              isHealthy ? (
                <CheckCircle className="w-6 h-6" />
              ) : (
                <AlertTriangle className="w-6 h-6" />
              )
            }
            color={toneTextClasses[systemStatusTone]}
            bg={toneIconBgClasses[systemStatusTone]}
          />
        </div>

        {/* System Health */}
        <Card className="p-6">
          <div className="flex flex-col items-start gap-2 mb-6 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
              <Activity className="text-brand-600 dark:text-brand-400" />{' '}
              {t('platformDashboard.systemHealth')}
            </h3>
            <span className="text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-wider inline-flex items-center gap-1 whitespace-nowrap">
              <Clock size={12} /> {t('platformDashboard.lastChecked')}:{' '}
              {new Date(metrics.system_health.last_checked).toLocaleTimeString(locale, {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <HealthIndicator
              label={t('platformDashboard.components.database')}
              status={metrics.system_health.database_status}
              icon={<Database size={24} />}
              t={t}
            />
            <HealthIndicator
              label={t('platformDashboard.components.redis')}
              status={metrics.system_health.redis_status}
              icon={<Server size={24} />}
              t={t}
            />
            <HealthIndicator
              label={t('platformDashboard.components.storage')}
              status={metrics.system_health.storage_status}
              icon={<HardDrive size={24} />}
              t={t}
            />
            <HealthIndicator
              label={t('platformDashboard.components.agents')}
              status={metrics.system_health.agent_status}
              icon={<Activity size={24} />}
              t={t}
            />
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Events */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                <Clock className="text-state-info-light dark:text-state-info-dark" />{' '}
                {t('platformDashboard.recentEvents')}
              </h3>
            </div>
            {metrics.recent_events.length === 0 ? (
              <p className="text-text-subtle-light dark:text-text-subtle-dark text-center py-8 font-medium">
                {t('platformDashboard.noRecentEvents')}
              </p>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto custom-scrollbar pr-2">
                {metrics.recent_events.map((event) => (
                  <div
                    key={event.id}
                    className="p-4 bg-surface-2-light dark:bg-surface-2-dark rounded-xl border border-surface-border-light dark:border-surface-border-dark"
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-bold text-slate-900 dark:text-white text-sm">
                        {translateEvent(event.type, t)}
                      </span>
                      <span className="text-xs text-text-subtle-light dark:text-text-subtle-dark font-mono">
                        {new Date(event.created_at).toLocaleString(locale)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {translateEvent(event.message, t)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Top Customers */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                <Users className="text-accent-text-light dark:text-accent-text-dark" />{' '}
                {t('platformDashboard.topCustomers')}
              </h3>
            </div>
            {metrics.top_customers.length === 0 ? (
              <p className="text-text-subtle-light dark:text-text-subtle-dark text-center py-8 font-medium">
                {t('platformDashboard.noCustomers')}
              </p>
            ) : (
              <div className="space-y-3">
                {metrics.top_customers.map((customer, idx) => (
                  <div
                    key={customer.organization_id}
                    className="flex items-center justify-between p-3 bg-surface-2-light dark:bg-surface-2-dark rounded-xl hover:bg-surface-card-light dark:hover:bg-surface-card-dark transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-lg font-black text-text-subtle-light dark:text-text-subtle-dark">
                        #{idx + 1}
                      </span>
                      <div>
                        <p className="font-bold text-slate-900 dark:text-white">{customer.name}</p>
                        <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
                          {t('platformDashboard.customerUsage', {
                            count: customer.backup_count,
                            backups: formatNumber(customer.backup_count),
                            storage: formatNumber(customer.storage_used, 2),
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-state-ok-light dark:text-state-ok-dark">
                        {formatCurrency(customer.monthly_revenue)}
                      </p>
                      <p className="text-xs text-text-subtle-light dark:text-text-subtle-dark font-bold uppercase">
                        {t('platformDashboard.monthly')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}

function StatCard({ label, value, icon, color, bg }: any) {
  return (
    <div
      className={`min-w-0 p-6 rounded-2xl ${bg} flex items-center justify-between gap-3 transition-transform hover:scale-[1.02]`}
    >
      <div className="min-w-0 flex-1">
        <p className="break-words text-2xl font-black leading-tight text-slate-900 dark:text-white 2xl:text-3xl">
          {value}
        </p>
        <p className={`text-xs font-bold uppercase leading-tight tracking-wider ${color}`}>
          {label}
        </p>
      </div>
      <div
        className={`shrink-0 p-3 rounded-xl bg-surface-card-light/50 dark:bg-surface-card-dark/50 ${color}`}
      >
        {icon}
      </div>
    </div>
  );
}

function HealthIndicator({ label, status, icon, t }: any) {
  const tone = getStatusTone(status);
  return (
    <div
      className={`p-4 rounded-xl border-2 flex flex-col items-center justify-center gap-2 transition-colors ${toneChipClasses[tone]}`}
    >
      {icon}
      <span className="font-black text-sm uppercase tracking-wider">{label}</span>
      <Badge variant={toneToBadgeVariant[tone]} className="text-[10px] uppercase">
        {translateStatus(status, t)}
      </Badge>
    </div>
  );
}

function translateStatus(status: string, t: (key: string) => string) {
  const key = `platformDashboard.status.${status.toLowerCase()}`;
  const translated = t(key);
  return translated === key ? status : translated;
}

function translateEvent(event: string, t: (key: string) => string) {
  const normalized = event
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
  const keys = [
    `platformDashboard.eventTypes.${normalized}`,
    `platformAnalytics.featureNames.${normalized}`,
  ];

  for (const key of keys) {
    const translated = t(key);
    if (translated !== key) return translated;
  }

  return event.replace(/[_]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}
